'use client';

import * as React from 'react';

// Using workspace defaults
const APP_NAME = "KithFlow";
const cn = (...classes: (string | undefined | boolean)[]) => classes.filter(Boolean).join(' ');

export type LogoElement = React.ComponentRef<'div'>;
export type LogoProps = React.ComponentPropsWithoutRef<'div'> & {
  hideSymbol?: boolean;
  hideWordmark?: boolean;
};

/**
 * AI-Centric Logo component for KithFlow.
 * Replaces the literal 'K' with an "Intelligence Spark" symbol.
 * The symbol represents generative power and fluid data flow.
 */
export function Logo({
  hideSymbol,
  hideWordmark,
  className,
  ...other
}: LogoProps): React.JSX.Element {
  return (
    <div
      className={cn('flex items-center gap-2.5 select-none group', className)}
      {...other}
    >
      {!hideSymbol && (
        <div className="flex size-8 items-center justify-center shrink-0">
          <svg
            width="28"
            height="28"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="text-primary transition-all duration-500 group-hover:scale-110"
          >
            {/* The AI Spark: Representing Intelligence and Generation */}
            <path
              d="M12 3L13.15 8.85L19 10L13.15 11.15L12 17L10.85 11.15L5 10L10.85 8.85L12 3Z"
              fill="currentColor"
              className="animate-pulse"
            />

            {/* The Flow Line: Representing the "Flow" of data and connection */}
            <path
              d="M19 14C19 14 17 19 12 19C7 19 5 14 5 14"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              className="opacity-60"
            />

            {/* Accent Sparkle for extra AI "Magic" feel */}
            <circle cx="20" cy="18" r="1.5" fill="currentColor" />
          </svg>
        </div>
      )}

      {!hideWordmark && (
        <div className="flex items-center">
          <span className="text-xl tracking-tight text-foreground">
            <span className="font-medium opacity-80">Kith</span>
            <span className="font-bold text-primary">Flow</span>
          </span>
        </div>
      )}
    </div>
  );
}

export default Logo;